// Eskom PARCC — audit trail (admin).
//
// Renders the append-only AuditLog: every change across the workspace (access
// grants, profile edits, transaction / working-paper / finding / CES / RFI / RTM
// writes, allocations, imports) with the SERVER-STAMPED actor ("By whom"). The
// actor is forge-proof — it is set from the verified auth token by the
// AuditLog.actorEmail/actorUid column defaults (client writes) or explicitly by
// the admin-api (privileged writes), never from client-supplied data. Admin-claim
// enforced server-side by PullAccessAuditLog. Read-only; filterable client-side.
import { useMemo, useState } from 'react';
import {
  DataTable,
  LoadingState,
  ErrorState,
  FilterBar,
  SelectFilter,
  SearchInput,
  type Column,
} from '../../components/ui';
import { useAccessAuditLog } from '../../hooks';
import type { AuditLogEntry } from '../../lib/db';
import { actionLabel } from '../audit/auditActions';

function formatAt(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('en-ZA');
}

export function AuditPanel(): React.JSX.Element {
  const auditQuery = useAccessAuditLog();
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const columns = useMemo<Column<AuditLogEntry>[]>(
    () => [
      { key: 'at', header: 'When', sortable: true, accessor: (r) => r.at, render: (r) => formatAt(r.at) },
      {
        key: 'actorEmail',
        header: 'By',
        sortable: true,
        accessor: (r) => r.actorEmail ?? '',
        render: (r) => r.actorEmail ?? '—',
      },
      {
        key: 'action',
        header: 'Action',
        sortable: true,
        accessor: (r) => actionLabel(r.action),
        render: (r) => actionLabel(r.action),
      },
      {
        key: 'entityType',
        header: 'Entity',
        sortable: true,
        accessor: (r) => r.entityType ?? '',
        render: (r) => r.entityType ?? '—',
      },
      {
        key: 'entityId',
        header: 'Target',
        sortable: true,
        accessor: (r) => r.entityId ?? '',
        render: (r) => r.entityId ?? '—',
      },
      { key: 'note', header: 'Detail', render: (r) => r.note ?? '—' },
    ],
    [],
  );

  const allRows = useMemo(() => auditQuery.data ?? [], [auditQuery.data]);

  // Distinct actions present, for the dropdown (labelled, sorted).
  const actionOptions = useMemo(() => {
    const present = Array.from(new Set(allRows.map((r) => r.action))).sort((a, b) =>
      actionLabel(a).localeCompare(actionLabel(b)),
    );
    return [
      { value: '', label: 'All actions' },
      ...present.map((a) => ({ value: a, label: actionLabel(a) })),
    ];
  }, [allRows]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allRows.filter((r) => {
      if (actionFilter && r.action !== actionFilter) return false;
      if (!term) return true;
      return [r.actorEmail, actionLabel(r.action), r.entityType, r.entityId, r.note].some((v) =>
        (v ?? '').toLowerCase().includes(term),
      );
    });
  }, [allRows, search, actionFilter]);

  if (auditQuery.isLoading) return <LoadingState message="Loading audit trail…" />;
  if (auditQuery.error) {
    const err = auditQuery.error;
    return (
      <ErrorState
        message="Could not load the audit trail."
        detail={err instanceof Error ? err.message : String(err)}
        onRetry={() => void auditQuery.refetch()}
      />
    );
  }

  return (
    <div className="card">
      <div className="section-head">
        <div>
          <h3>Audit trail</h3>
          <div className="small">
            {rows.length.toLocaleString('en-ZA')}
            {rows.length !== allRows.length ? ` of ${allRows.length.toLocaleString('en-ZA')}` : ''} event(s)
          </div>
        </div>
      </div>
      <FilterBar>
        <SearchInput label="Search" value={search} onChange={setSearch} placeholder="By, target, detail…" />
        <SelectFilter label="Action" value={actionFilter} options={actionOptions} onChange={setActionFilter} />
      </FilterBar>
      <DataTable
        columns={columns}
        rows={rows}
        getRowKey={(r) => r.id}
        initialSort={{ key: 'at', dir: 'desc' }}
        emptyMessage="No activity recorded yet."
      />
    </div>
  );
}
